import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, Users, Wallet, Stethoscope, Wrench,
  Scissors, Building, CheckCircle, Instagram, Search,
  Target, Sparkles, ArrowRight, Palette
} from 'lucide-react';
import { CodeText } from './TextAnimations';
import { useTranslation } from 'react-i18next';

// Section ROI avec glassmorphism ultra-premium
export const ROISection = () => {
  const { t } = useTranslation();
  const results = [
    { value: "+35", suffix: "%", labelKey: "roi.rdv", icon: TrendingUp, color: "from-cyan-500/20 via-sky-500/20 to-cyan-600/20" },
    { value: "-60", suffix: "%", labelKey: "roi.lost", icon: Users, color: "from-indigo-500/20 via-violet-500/20 to-indigo-600/20" },
    { value: "+20", suffix: "%", labelKey: "roi.clients", icon: Wallet, color: "from-violet-500/20 via-purple-500/20 to-violet-600/20" }
  ];

  return (
    <section className="py-20 px-4 relative overflow-hidden">
      {/* Static gradient background */}
      <div
        className="absolute inset-0 opacity-30"
        style={{ background: 'radial-gradient(circle at 50% 50%, rgba(6, 182, 212, 0.03) 0%, transparent 50%)' }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.h2
            className="text-4xl md:text-5xl font-black mb-4 tracking-tight"
            initial={{ scale: 0.9 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
          >
            <CodeText text={t('roi.title')} className="gradient-callup" delay={0} />
          </motion.h2>
          <p className="text-gray-300 text-xl font-medium tracking-wide">{t('roi.subtitle')}</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {results.map((result, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0, duration: 0.3 }}
              whileHover={{ y: -15, scale: 1.05 }}
              className="relative group"
            >
              {/* Glassmorphism card premium */}
              <motion.div
                className={`p-8 rounded-3xl bg-gradient-to-br ${result.color} backdrop-blur-2xl relative overflow-hidden border border-white/20 shadow-2xl`}
                whileHover={{ 
                  boxShadow: '0 30px 80px rgba(0,0,0,0.5), 0 0 60px rgba(56, 189, 248, 0.3)',
                  borderColor: 'rgba(255,255,255,0.4)'
                }}
                style={{
                  background: `linear-gradient(135deg, rgba(6, 182, 212, 0.1) 0%, rgba(139, 92, 246, 0.15) 100%)`
                }}
              >
                {/* Inner glow effect */}
                <div
                  className="absolute inset-0 rounded-3xl"
                  style={{
                    background: 'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.1) 0%, transparent 70%)',
                    opacity: 0.4
                  }}
                />

                <div className="relative z-10">
                  <result.icon className="w-12 h-12 text-white mb-4 mx-auto drop-shadow-lg" />
                  <motion.div
                    className="text-5xl md:text-6xl font-black text-white mb-3 text-center"
                    initial={{ scale: 0.9 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0, duration: 0.2 }}
                    style={{
                      textShadow: '0 4px 20px rgba(0,0,0,0.5), 0 0 40px rgba(255,255,255,0.2)',
                      letterSpacing: '0.02em'
                    }}
                  >
                    {result.value}{result.suffix}
                  </motion.div>
                  <p className="text-white text-base md:text-lg font-semibold text-center tracking-wide">{t(result.labelKey)}</p>
                </div>

                {/* Floating particles premium - Supprimé pour performance */}
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Section Pour Qui - Glassmorphism Premium
export const ForWhoSection = () => {
  const { t } = useTranslation();
  const [hoveredCard, setHoveredCard] = useState(null);

  const sectors = [
    { icon: Stethoscope, titleKey: "for_who.doctors", problemKey: "for_who.doctors_problem", solutionKey: "for_who.doctors_solution", color: "from-cyan-500/10 via-sky-500/10 to-cyan-600/10" },
    { icon: Wrench, titleKey: "for_who.garages", problemKey: "for_who.garages_problem", solutionKey: "for_who.garages_solution", color: "from-indigo-500/10 via-violet-500/10 to-indigo-600/10" },
    { icon: Scissors, titleKey: "for_who.hairdressers", problemKey: "for_who.hairdressers_problem", solutionKey: "for_who.hairdressers_solution", color: "from-violet-500/10 via-purple-500/10 to-violet-600/10" },
    { icon: Building, titleKey: "for_who.agencies", problemKey: "for_who.agencies_problem", solutionKey: "for_who.agencies_solution", color: "from-purple-600/10 via-fuchsia-600/10 to-purple-700/10" }
  ];

  return (
    <section className="py-20 px-4 relative">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-black mb-4 tracking-tight">
            <CodeText text={t('for_who.title')} className="gradient-callup" delay={0} />
          </h2>
          <p className="text-gray-300 text-xl font-medium tracking-wide">{t('for_who.subtitle')}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {sectors.map((sector, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0, duration: 0.3 }}
              onHoverStart={() => setHoveredCard(i)}
              onHoverEnd={() => setHoveredCard(null)}
              className="relative perspective-1000"
            >
              <motion.div
                className={`p-8 rounded-2xl bg-gradient-to-br ${sector.color} backdrop-blur-xl relative overflow-hidden border border-white/10 shadow-2xl`}
                animate={{
                  rotateY: hoveredCard === i ? 5 : 0,
                  scale: hoveredCard === i ? 1.03 : 1,
                  z: hoveredCard === i ? 50 : 0
                }}
                whileHover={{
                  boxShadow: '0 20px 60px rgba(0,0,0,0.4), 0 0 40px rgba(139, 92, 246, 0.2)',
                  borderColor: 'rgba(255,255,255,0.3)'
                }}
                transition={{ duration: 0.3 }}
                style={{
                  transformStyle: 'preserve-3d',
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.12) 100%)'
                }}
              >
                {/* Luminous border effect */}
                <motion.div
                  className="absolute inset-0 rounded-2xl"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)',
                    opacity: hoveredCard === i ? 1 : 0
                  }}
                  transition={{ duration: 0.3 }}
                />

                <div className="relative z-10">
                  <motion.div
                    className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center mb-4 border border-white/20"
                    animate={{
                      rotate: hoveredCard === i ? 360 : 0
                    }}
                    transition={{ duration: 0.6 }}
                  >
                    <sector.icon className="w-8 h-8 text-white drop-shadow-lg" />
                  </motion.div>

                  <h3 className="text-xl md:text-2xl font-black text-white mb-3 tracking-tight">{t(sector.titleKey)}</h3>

                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-red-500/20 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 border border-red-500/30">
                        <span className="text-white font-bold text-xs">✗</span>
                      </div>
                      <p className="text-white/90 text-sm font-medium">{t(sector.problemKey)}</p>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 border border-green-500/30">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <p className="text-white font-semibold text-sm">{t(sector.solutionKey)}</p>
                    </div>
                  </div>
                </div>

                {/* Ambient light effect */}
                <motion.div
                  className="absolute -inset-1 rounded-2xl opacity-0"
                  animate={{
                    opacity: hoveredCard === i ? 0.3 : 0
                  }}
                  style={{
                    background: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.4), transparent 70%)',
                    filter: 'blur(20px)',
                    zIndex: -1
                  }}
                />
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Section Labs Agency Marketing
export const LabsMarketingSection = () => {
  const { t } = useTranslation();
  const services = [
    { icon: Search, titleKey: "labs_marketing.visibility", items: ["Google Business Profile", t('labs_marketing.visibility_f2'), t('labs_marketing.visibility_f3')], color: "from-yellow-400 to-orange-500" },
    { icon: Instagram, titleKey: "labs_marketing.social", items: [t('labs_marketing.social_f1'), t('labs_marketing.social_f2'), t('labs_marketing.social_f3')], color: "from-pink-500 to-purple-500" },
    { icon: Target, titleKey: "labs_marketing.ads", items: ["Meta Ads (FB/IG)", "Google Ads", t('labs_marketing.ads_f3')], color: "from-sky-500 to-blue-500" },
    { icon: Palette, titleKey: "labs_marketing.art_direction", items: [t('labs_marketing.art_f1'), t('labs_marketing.art_f2'), t('labs_marketing.art_f3'), t('labs_marketing.art_f4')], color: "from-violet-500 to-fuchsia-500" }
  ];

  return (
    <section className="py-20 px-4 bg-gradient-to-br from-orange-50 to-pink-50">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-400 to-pink-400 text-white rounded-full mb-6">
            <Sparkles className="w-5 h-5" />
            <span className="font-bold">Labs Agency</span>
          </div>

          <h2 className="text-4xl md:text-5xl font-black mb-4 gradient-labs">
            {t('labs_marketing.title')}
          </h2>
          <p className="text-gray-600 text-lg">{t('labs_marketing.subtitle')}</p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              whileHover={{ y: -10, scale: 1.02 }}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-shadow"
            >
              <motion.div
                className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-4`}
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
              >
                <service.icon className="w-7 h-7 text-white" />
              </motion.div>

              <h3 className="text-xl font-black mb-4">{t(service.titleKey)}</h3>

              <ul className="space-y-3">
                {service.items.map((item, j) => (
                  <li key={j} className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>

              <motion.div
                className="mt-6"
                whileHover={{ x: 5 }}
              >
                <a href="#calendly" className="text-sm font-bold text-orange-500 inline-flex items-center gap-1">
                  {t('labs_marketing.learn_more')} <ArrowRight className="w-4 h-4" />
                </a>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

